import type { AxiosInstance } from 'axios';

import type { OrderStatus } from '@accio3d/shared/domain';
import type { FunctionalRequirements } from '@accio3d/shared-ui/printability/services/printability3dService';
import type { SortDir } from '@accio3d/shared-ui/components/ui/sort-direction-select';
import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';
import type { SupplierOrderPart } from './supplier';

/** One part on a purchaser's order list row — the lean fields the list card renders. */
export interface PurchaserOrderListPart {
  manufacturingRequirementId: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  material: string | null;
  processType: string | null;
  quantity: number;
  partLengthMm: number | null;
  partWidthMm: number | null;
  partHeightMm: number | null;
  pricePerUnit: number | null;
  lineTotal: number | null;
}

export interface PurchaserOrder {
  id: string;
  quoteId: string;
  orderNumber: string;
  rfqNumber: string;
  partId: string;
  partName: string;
  partNumber: string | null;
  status: string;
  progress: number;
  quantity: number;
  amountPaid: number;
  subTotal: number;
  tax: number | null;
  shippingCost: number | null;
  currency: string;
  shippingAddress: string | null;
  supplierName: string;
  leadTimeDays: number | null;
  createdAt: string;
  estimatedDelivery: string | null;
  parts: PurchaserOrderListPart[];
}

export interface OrderSupplierDetail {
  name: string;
  location: string | null;
  tier: string | null;
  rating: number | null;
  ratingCount: number | null;
  certifications: string[];
  technologies: string[];
}

export interface OrderTrackingInfo {
  carrier: string;
  trackingCode: string | null;
}

/** One part on a purchaser's order, frozen from the accepted quote's lines. */
export interface PurchaserOrderPart {
  manufacturingRequirementId: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  material: string | null;
  processType: string | null;
  color: string | null;
  infill: number | null;
  postProcesses: string[];
  quantity: number;
  partLengthMm: number | null;
  partWidthMm: number | null;
  partHeightMm: number | null;
  partMassG: number | null;
  tightestToleranceMm: number | null;
  notes: string | null;
  documentFileUrls: string[];
  pricePerUnit: number | null;
  lineTotal: number | null;
  functionalRequirements: FunctionalRequirements | null;
}

export interface PurchaserOrderDetail extends PurchaserOrder {
  rfqId: string;
  rfqName: string | null;
  notes: string | null;
  parts: PurchaserOrderPart[];
  shippingCarrier: string | null;
  shippingService: string | null;
  isReconstruction: boolean;
  deliverableUrl: string | null;
  supplier: OrderSupplierDetail;
  tracking: OrderTrackingInfo | null;
}

// Subset of the API's OrderResponse the supplier orders page consumes.
export interface SupplierOrderListItem {
  id: string;
  orderNumber: string;
  parts: SupplierOrderPart[];
  /** Purchaser org name; null when the relation wasn't loaded */
  customerName: string | null;
  dueDate: string | null;
  status: OrderStatus;
  paymentStatus: 'pending' | 'paid';
  createdAt: string;
}

export interface OrderStats {
  total: number;
  byStatus: Record<OrderStatus, number>;
}

export interface OrderListFilters {
  search?: string;
  status?: OrderStatus;
  sortDir?: SortDir;
}

export function createOrdersService(client: AxiosInstance) {
  return {
    async listForPurchaser(
      pagination?: PaginationParams,
      filters?: OrderListFilters,
    ): Promise<PagePaginatedResponse<PurchaserOrder>> {
      const params = Object.fromEntries(
        Object.entries(filters ?? {}).filter(([, value]) => value != null && value !== ''),
      );
      return fetchPaginatedList<PurchaserOrder>(
        client,
        '/purchasers/orders',
        pagination,
        Object.keys(params).length ? { params } : undefined,
      );
    },

    async get(id: string): Promise<PurchaserOrderDetail> {
      const { data } = await client.get<PurchaserOrderDetail>(`/purchasers/orders/${id}`);
      return data;
    },

    async listForSupplier(
      pagination?: PaginationParams,
      filters?: OrderListFilters,
    ): Promise<PagePaginatedResponse<SupplierOrderListItem>> {
      // Filtering/sorting is server-side; drop unset keys so the URL stays clean.
      const params = Object.fromEntries(
        Object.entries(filters ?? {}).filter(([, value]) => value != null && value !== ''),
      );
      return fetchPaginatedList<SupplierOrderListItem>(
        client,
        '/suppliers/orders',
        pagination,
        Object.keys(params).length ? { params } : undefined,
      );
    },

    async statsForSupplier(): Promise<OrderStats> {
      const { data } = await client.get<OrderStats>('/suppliers/orders/stats');
      return data;
    },

    async statsForPurchaser(): Promise<OrderStats> {
      const { data } = await client.get<OrderStats>('/purchasers/orders/stats');
      return data;
    },
  };
}

export type OrdersService = ReturnType<typeof createOrdersService>;
